import { useCallback, useContext, useEffect, useState } from "react";
import { useAuth } from "react-oidc-context";
import { t } from "i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import debounce from "lodash/debounce";
import {
  faPencilAlt,
  faRefresh,
  faLock,
  faLockOpen,
} from "@fortawesome/pro-regular-svg-icons";
//@ts-ignore
import Pagination from "react-js-pagination";
import { AnalyticsWidgetUiDetailsDto } from "../../../../types/analytics";
import {
  NineBoxGridEditorAxisSettings,
  NineBoxGridEditorDataItem,
  NineBoxGridEditorDataItemAxisScore,
  NineBoxGridEditorSummary,
  NineBoxGridOverrideScorePopupData,
  NineBoxGridScoreOverrideAxis,
} from "../../../../types/analytics/charts/NineBoxGridEditorData";

import TinyLoader from "../../../loaders/TinyLoader";
import UserContext from "../../../../state/UserContext";
import SearchField from "../../../common/SearchField";
import UserProfileHoverCard from "../../../common/UserProfileHoverCard";
import NineBoxGridScoreOverridePopup from "./NineBoxGridScoreOverridePopup";
import analyticsDynamicWidgetApi from "../../../../api/analytics/analyticsDynamicWidgetApi";
import { AnalyticsWidgetRefreshArguments } from "../../../../types/analytics/AnalyticsWidgetRefreshFilterParams";
import AnalyticsFilterInputs from "../../../../types/analytics/AnalyticsFilterInputs";
import { Tooltip, ModalPopup } from "../../../common";
import { AnalyticsTableSearchPageDetails } from "../../../../types/analytics/tables/AnalyticsTableSearchPageDetails";
import permissionHelper from "../../../../helpers/permissionHelper";
import nineBoxGridEditorApi from "../../../../api/analytics/nineBoxGridEditorApi";
import Toast from "../../../common/Toast";
import { ToastConfig } from "../../../common/ToastConfig";
import { displayEmployeeDimensionLabel } from "../../../../helpers/analytics/displayEmployeeDimensionLabel";

export interface NineBoxGridEditorProps {
  widget: AnalyticsWidgetUiDetailsDto;
  analyticsPageId: number;
  filters: AnalyticsFilterInputs;
  /** If there's a 9BG widget on the same page, supply the ID so we can use it to refresh the data for that widget from this one */
  nineBoxGridWidgetId: number | null;
  onRequestOtherWidgetRefresh?: (
    dynamicWidgetId: number,
    onSuccess: () => void,
    onError: () => void,
  ) => void;
}

function NineBoxGridEditor({
  widget,
  analyticsPageId,
  filters,
  nineBoxGridWidgetId,
  onRequestOtherWidgetRefresh,
}: NineBoxGridEditorProps) {
  const userContext = useContext(UserContext);
  const auth = useAuth();
  const dynamicWidgetApi = new analyticsDynamicWidgetApi(
    auth.user?.access_token,
    analyticsPageId,
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const pageSize = 15;
  const [pageSearchDetails, setPageSearchDetails] =
    useState<AnalyticsTableSearchPageDetails>({
      pageNumber: 1,
      searchPhrase: null,
      refreshDataFromApi: false,
    });
  const [totalItemCount, setTotalItemCount] = useState<number>(0);
  const [displayData, setDisplayData] = useState<NineBoxGridEditorDataItem[]>(
    [],
  );
  const [axisSettings, setAxisSettings] =
    useState<NineBoxGridEditorAxisSettings | null>(null);

  const [scoreOverridePopupData, setScoreOverridePopupData] =
    useState<NineBoxGridOverrideScorePopupData | null>(null);

  const [hasChangedScores, setHasChangedScores] = useState<boolean>(false);
  const [isRefreshingNineBoxGrid, setIsRefreshingNineBoxGrid] =
    useState<boolean>(false);

  // Lock/unlock state
  const [isEditorLocked, setIsEditorLocked] = useState<boolean>(false);
  const [showLockConfirmModal, setShowLockConfirmModal] =
    useState<boolean>(false);
  const [showUnlockConfirmModal, setShowUnlockConfirmModal] =
    useState<boolean>(false);
  const [isUpdatingLockState, setIsUpdatingLockState] =
    useState<boolean>(false);
  const [toast, setToast] = useState<ToastConfig>(ToastConfig.hidden());
  const nineBoxGridApi = new nineBoxGridEditorApi(auth.user?.access_token);

  // Check if user has AdminNineBoxGridEditor permission
  const hasAdminPermission = permissionHelper.userHasEitherPermission(
    userContext,
    "AdminNineBoxGridEditor",
    "ADMIN-NINE-BOX-GRID-EDITOR",
  );

  useEffect(() => {
    initialiseData(widget?.datasets);
  }, [widget.datasets]);

  useEffect(() => {
    // Initialize lock state from widget config
    const lockConfig = widget?.configs?.find(
      (config) => config.key === "EDITOR-IS-LOCKED",
    );
    setIsEditorLocked(lockConfig?.value === "TRUE");
  }, [widget.configs]);

  useEffect(() => {
    if (pageSearchDetails.refreshDataFromApi) {
      refreshData();
    }
  }, [pageSearchDetails]);

  const initialiseData = (datasets: any[] | undefined) => {
    // Handle the "no data" scenario,
    // or scenarios where the data is not in the expected format
    // with the first dataset being the items to display,
    // and the second "dataset" object being the summary
    if (!datasets || datasets.length !== 2) {
      setDisplayData([]);
      setTotalItemCount(0);
      setAxisSettings(null);
      return;
    }

    const dataItems = datasets[0] as NineBoxGridEditorDataItem[];
    const summary = datasets[1] as NineBoxGridEditorSummary;
    setDisplayData(dataItems);
    setTotalItemCount(summary.totalItemCount);
    setAxisSettings(summary.axisSettings);
    setIsLoading(false);
  };

  const refreshData = () => {
    setIsLoading(true);

    const successCallback = (data: AnalyticsWidgetUiDetailsDto) => {
      initialiseData(data.datasets);
      setIsLoading(false);
    };

    const errorCallback = (error: any) => {
      setIsLoading(false);
      console.error(error);
    };

    const refreshParams: AnalyticsWidgetRefreshArguments = {
      analyticsPageId: analyticsPageId,
      widgetId: widget.dynamicWidgetId!,
      pageNumber: pageSearchDetails.pageNumber,
      pageSize: pageSize,
      searchValue: pageSearchDetails.searchPhrase,
    };

    dynamicWidgetApi.refreshWidgetData(
      filters,
      refreshParams,
      successCallback,
      errorCallback,
    );
  };

  /** Debounce the actual state change so it doesn't call the API to refresh the data on each keystroke */
  const debouncedUpdateSearchState = useCallback(
    debounce((searchValue: string | null) => {
      // Reset page number too
      setPageSearchDetails({
        pageNumber: 1,
        searchPhrase: searchValue,
        refreshDataFromApi: true,
      });
    }, 300),
    [],
  );

  const handleSearchChange = (newSearchPhrase: string | null) => {
    debouncedUpdateSearchState(newSearchPhrase);
  };

  const handlePageChange = (pageNumber: number) => {
    setPageSearchDetails({
      ...pageSearchDetails,
      pageNumber,
      refreshDataFromApi: true,
    });
  };

  const openScoreOverrideModal = (
    rowData: NineBoxGridEditorDataItem,
    axis: NineBoxGridScoreOverrideAxis,
    isOpen: boolean,
  ) => {
    setScoreOverridePopupData({
      modalIsOpen: isOpen,
      dataItem: rowData,
      editAxis: axis,
    });
  };

  const clearScoreOverrideModalData = () => {
    setScoreOverridePopupData(null);
  };

  const onUpdateScore = (
    editRow: NineBoxGridEditorDataItem,
    axis: NineBoxGridScoreOverrideAxis,
    newScore: NineBoxGridEditorDataItemAxisScore,
    newBoxNumber: number,
  ) => {
    // If there is popup data, then we need to update the score in the UI based on the mode
    if (scoreOverridePopupData && scoreOverridePopupData.editAxis) {
      // Find the complex score in the display data, update score and set display data
      const updatedData = displayData.map((row) => {
        if (row.employeeId === editRow.employeeId) {
          row.boxNumber = newBoxNumber;
          if (axis === "X") {
            row.xScore = newScore;
          } else {
            row.yScore = newScore;
          }
        }

        return row;
      });
      setDisplayData(updatedData);
      setHasChangedScores(true);

      // Clear the modal data
      clearScoreOverrideModalData();
    }
  };

  const onRefreshNineBoxGridClick = () => {
    if (!onRequestOtherWidgetRefresh || !nineBoxGridWidgetId) return;
    const onRefreshSuccess = () => {
      setIsRefreshingNineBoxGrid(false);
      // 9BG refreshed, so hide the button now there aren't pending changes
      setHasChangedScores(false);
    };

    const onRefreshError = () => {
      setIsRefreshingNineBoxGrid(false);
      console.error("Error refreshing Nine Box Grid widget");
    };

    setIsRefreshingNineBoxGrid(true);
    onRequestOtherWidgetRefresh(
      nineBoxGridWidgetId,
      onRefreshSuccess,
      onRefreshError,
    );
  };

  const handleLockButtonClick = () => {
    if (isEditorLocked) {
      setShowUnlockConfirmModal(true);
    } else {
      setShowLockConfirmModal(true);
    }
  };

  const confirmLockEditor = () => {
    if (!widget.dynamicWidgetId) return;

    setIsUpdatingLockState(true);
    nineBoxGridApi.upsertEditorLockState(
      widget.dynamicWidgetId,
      true,
      () => {
        setIsEditorLocked(true);
        setIsUpdatingLockState(false);
        setShowLockConfirmModal(false);
        setToast(
          ToastConfig.success(
            "Editor Locked",
            "The editor has been successfully locked. Users can view scores but cannot make changes.",
          ),
        );
        // Refresh widget to get updated config
        refreshData();
      },
      (error) => {
        console.error("Error locking editor:", error);
        setIsUpdatingLockState(false);
        setToast(
          ToastConfig.error(
            "Lock Failed",
            "There was an error locking the editor. Please try again.",
          ),
        );
      },
    );
  };

  const confirmUnlockEditor = () => {
    if (!widget.dynamicWidgetId) return;

    setIsUpdatingLockState(true);
    nineBoxGridApi.upsertEditorLockState(
      widget.dynamicWidgetId,
      false,
      () => {
        setIsEditorLocked(false);
        setIsUpdatingLockState(false);
        setShowUnlockConfirmModal(false);
        setToast(
          ToastConfig.success(
            "Editor Unlocked",
            "The editor has been successfully unlocked. Users can now make changes to scores.",
          ),
        );
        // Refresh widget to get updated config
        refreshData();
      },
      (error) => {
        console.error("Error unlocking editor:", error);
        setIsUpdatingLockState(false);
        setToast(
          ToastConfig.error(
            "Unlock Failed",
            "There was an error unlocking the editor. Please try again.",
          ),
        );
      },
    );
  };

  const noResultsLoaded = !isLoading && totalItemCount === 0;
  // Data snapshot is read-only: never edit historical scores, regardless of admin lock permission.
  const isSnapshotLocked = (filters.pointInTimeDate?.trim() ?? "") !== "";
  const isEffectivelyLocked = isEditorLocked || isSnapshotLocked;
  const showAdminLockChrome =
    hasAdminPermission && !noResultsLoaded && displayData.length > 0;

  return (
    <>
      <div className="flex items-center gap-2 my-0.5">
        <SearchField
          onSearch={handleSearchChange}
          isLoading={isLoading}
          loaderColor={userContext.user.client.accentHexColour}
          outerContainerClassName="flex items-center my-0.5 flex-1"
        />
        {showAdminLockChrome &&
          (isSnapshotLocked ? (
            <Tooltip
              content={t(
                "Analytics.Widgets.NineBoxGridEditor.LockedBySnapshotTooltip",
                "Scores cannot be changed while a data snapshot is applied.",
              )}
              triggerElement={
                <span className="inline-flex items-center gap-1.5 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm font-semibold text-amber-900">
                  <FontAwesomeIcon icon={faLock} size="sm" />
                  {t(
                    "Analytics.Widgets.NineBoxGridEditor.LockedBySnapshot",
                    "Locked - data snapshot",
                  )}
                </span>
              }
            />
          ) : (
            <button
              className="btn-secondary flex items-center gap-1.5 px-3 py-2 text-sm"
              onClick={handleLockButtonClick}
              disabled={isUpdatingLockState}
            >
              <FontAwesomeIcon
                icon={isEditorLocked ? faLockOpen : faLock}
                size="sm"
              />
              <span>{isEditorLocked ? "Unlock Editor" : "Lock Editor"}</span>
            </button>
          ))}
      </div>
      {noResultsLoaded && (
        <em className="block text-center italic py-4">
          {t("Pages.Analytics.Common.NoTableData")}
        </em>
      )}
      {!noResultsLoaded && displayData.length > 0 && (
        <div className="mb-4">
          <div className="relative border rounded-lg mt-2 mb-4">
            <div className="overflow-x-auto">
              <table className="w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="h-9 px-6 text-xs font-medium text-gray-500 uppercase tracking-wider border-r last:border-r-0 text-left">
                      Employee
                    </th>
                    <th className="h-9 px-6 text-xs font-medium text-gray-500 uppercase tracking-wider border-r last:border-r-0 text-left">
                      {t(
                        "Analytics.Widgets.Common.Tables.Columns.AppraisalLevel",
                      )}
                    </th>
                    <th className="h-9 px-6 text-xs font-medium text-gray-500 uppercase tracking-wider border-r last:border-r-0 text-left">
                      {t("Analytics.Widgets.Common.Tables.Columns.JobTitle")}
                    </th>
                    <th className="h-9 px-6 text-xs font-medium text-gray-500 uppercase tracking-wider border-r last:border-r-0 text-left">
                      {t("Analytics.Widgets.Common.Tables.Columns.Location")}
                    </th>
                    <th className="h-9 px-6 text-xs font-medium text-gray-500 uppercase tracking-wider border-r last:border-r-0 text-left">
                      {t(
                        "Analytics.Widgets.Common.Tables.Columns.PerformanceScore",
                      )}
                    </th>
                    <th className="h-9 px-6 text-xs font-medium text-gray-500 uppercase tracking-wider border-r last:border-r-0 text-left">
                      {t(
                        "Analytics.Widgets.Common.Tables.Columns.PotentialScore",
                      )}
                    </th>
                    <th className="h-9 px-6 text-xs font-medium text-gray-500 uppercase tracking-wider border-r last:border-r-0 text-left">
                      {t("Analytics.Widgets.Common.Tables.Columns.Box")}
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {displayData.map((row, rowIx) => (
                    <tr key={`${rowIx}`} className="hover:bg-gray-50">
                      <td className="min-h-[2.5rem] px-6 py-2.5 text-sm border-r last:border-r-0 text-gray-500">
                        <UserProfileHoverCard
                          employeeId={row.employeeId.toString()}
                        >
                          {row.firstName} {row.lastName}
                        </UserProfileHoverCard>
                      </td>
                      <td className="min-h-[2.5rem] px-6 py-2.5 text-sm border-r last:border-r-0 text-gray-500">
                        {displayEmployeeDimensionLabel(
                          row.appraisalLevel,
                          isSnapshotLocked,
                          t,
                        )}
                      </td>
                      <td className="min-h-[2.5rem] px-6 py-2.5 text-sm border-r last:border-r-0 text-gray-500">
                        {displayEmployeeDimensionLabel(
                          row.jobTitle,
                          isSnapshotLocked,
                          t,
                        )}
                      </td>
                      <td className="min-h-[2.5rem] px-6 py-2.5 text-sm border-r last:border-r-0 text-gray-500">
                        {row.location}
                      </td>
                      <td className="min-h-[2.5rem] px-6 py-2.5 text-sm border-r last:border-r-0 text-gray-900">
                        <NineBoxGridEditorEditButton
                          row={row}
                          axis="X"
                          openScoreOverrideModal={openScoreOverrideModal}
                          isLocked={isEffectivelyLocked}
                        />
                      </td>
                      <td className="min-h-[2.5rem] px-6 py-2.5 text-sm border-r last:border-r-0 text-gray-900">
                        <NineBoxGridEditorEditButton
                          row={row}
                          axis="Y"
                          openScoreOverrideModal={openScoreOverrideModal}
                          isLocked={isEffectivelyLocked}
                        />
                      </td>
                      <td className="min-h-[2.5rem] px-6 py-2.5 text-sm border-r last:border-r-0 text-gray-500">
                        {t(
                          `Analytics.Widgets.NineBoxGrid.BoxTitles.Box${row.boxNumber}`,
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <div className="flex flex-row">
            <div>
              {totalItemCount > pageSize && (
                <Pagination
                  activePage={pageSearchDetails.pageNumber}
                  itemsCountPerPage={pageSize}
                  totalItemsCount={totalItemCount}
                  pageRangeDisplayed={5}
                  onChange={handlePageChange}
                  activeClass="pagination-active-list-item"
                  itemClass="pagination-list-item"
                  itemClassFirst="pagination-first-item"
                  itemClassLast="pagination-last-item"
                />
              )}
            </div>
            {isLoading && (
              <div className="pl-4">
                <TinyLoader colour={userContext.user.client.accentHexColour} />
                <span className="ml-1 text-gray-500 text-sm">
                  {t("Common.Loading")}...
                </span>
              </div>
            )}
            {hasChangedScores && nineBoxGridWidgetId && (
              <div className="sm:hidden lg:block flex-grow pl-2 text-right">
                <button
                  className="btn-primary !px-2 group"
                  disabled={isRefreshingNineBoxGrid}
                >
                  <FontAwesomeIcon
                    icon={faRefresh}
                    size="xs"
                    className="group-hover:rotate-45 group-disabled:animate-spin"
                  />
                  <span className="ml-1" onClick={onRefreshNineBoxGridClick}>
                    Refresh Nine Box Grid
                  </span>
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Score Override Modal */}
      {scoreOverridePopupData &&
        scoreOverridePopupData.dataItem &&
        scoreOverridePopupData.editAxis &&
        axisSettings && (
          <NineBoxGridScoreOverridePopup
            analyticsPageId={analyticsPageId}
            dynamicWidgetId={widget.dynamicWidgetId!}
            model={scoreOverridePopupData}
            onSuccessfulUpdate={onUpdateScore}
            onCloseModal={clearScoreOverrideModalData}
            axisSettings={axisSettings}
            isLocked={isEffectivelyLocked}
          />
        )}

      {/* Lock Confirmation Modal */}
      <ModalPopup
        isOpen={showLockConfirmModal}
        onOpenChange={setShowLockConfirmModal}
        title="Lock Editor"
        primaryButtonText="Yes"
        secondaryButtonText="Cancel"
        onPrimaryButtonClick={confirmLockEditor}
        onSecondaryButtonClick={() => setShowLockConfirmModal(false)}
        isLoading={isUpdatingLockState}
        isPrimaryButtonDisabled={isUpdatingLockState}
        width="SMALL"
      >
        <p className="text-sm text-gray-600">
          Are you sure you want to lock editing, and make this read-only?
        </p>
      </ModalPopup>

      {/* Unlock Confirmation Modal */}
      <ModalPopup
        isOpen={showUnlockConfirmModal}
        onOpenChange={setShowUnlockConfirmModal}
        title="Unlock Editor"
        primaryButtonText="Yes"
        secondaryButtonText="Cancel"
        onPrimaryButtonClick={confirmUnlockEditor}
        onSecondaryButtonClick={() => setShowUnlockConfirmModal(false)}
        isLoading={isUpdatingLockState}
        isPrimaryButtonDisabled={isUpdatingLockState}
        width="SMALL"
      >
        <p className="text-sm text-gray-600">
          Are you sure you want to unlock editing?
        </p>
      </ModalPopup>

      {/* Toast notifications */}
      {toast.show && (
        <Toast
          title={toast.title}
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(ToastConfig.hidden())}
        />
      )}
    </>
  );
}

interface NineBoxGridEditorEditButtonProps {
  row: NineBoxGridEditorDataItem;
  axis: NineBoxGridScoreOverrideAxis;
  openScoreOverrideModal(
    rowData: NineBoxGridEditorDataItem,
    axis: NineBoxGridScoreOverrideAxis,
    isOpen: boolean,
  ): void;
  isLocked: boolean;
}

const NineBoxGridEditorEditButton = ({
  row,
  axis,
  openScoreOverrideModal,
  isLocked,
}: NineBoxGridEditorEditButtonProps) => {
  const displayValue =
    axis === "X" ? row.xScore.displayValue : row.yScore.displayValue;
  const tooltipValue =
    axis === "X" ? row.xScore.tooltipValue : row.yScore.tooltipValue;

  return (
    <button
      className="cursor-pointer primary-colour underline underline-offset-2 decoration-dotted hover:decoration-solid group"
      onClick={() => openScoreOverrideModal(row, axis, true)}
    >
      <Tooltip
        triggerElement={
          <span className="flex flex-row">
            <span className="flex-grow">{displayValue}</span>
            {!isLocked && (
              <span className="flex-initial">
                <FontAwesomeIcon
                  icon={faPencilAlt}
                  size="sm"
                  className="ml-1 invisible group-hover:visible text-gray-500"
                />
              </span>
            )}
          </span>
        }
        content={tooltipValue}
      />
    </button>
  );
};

export default NineBoxGridEditor;
